/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Box, Drawer, Grid, Typography, useTheme } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import TuneIcon from '@material-ui/icons/Tune';
import { Link } from 'gatsby';
import { Dispatch, Fragment, SetStateAction, useState } from 'react';
import { DATE_FORMAT, JobActionsType, JobStatusType } from '../..';
import {
  GetJobsInput,
  Job,
  JobLocation,
} from '../../../../../__generated__/pgeplus-types';
import {
  TableControlsActions,
  TableControlsType,
} from '../../../../../hooks/pge-plus/useJobsList';
import { PageTextContentType } from '../../../../../hooks/useContentMgmt';
import routes from '../../../../../routes';
import colors from '../../../../../themes/main-colors';
import { toDateString } from '../../../../../util/format';
import { FullDivider } from '../../../../pge-plus-common/FullDivider';
import { Filter } from '../controls/Filter';
import { Search } from '../controls/Search';
import { Sort } from '../controls/Sort';

type MobileViewProps = {
  content: PageTextContentType;
  tableControls: TableControlsType;
  jobActions: JobActionsType;
  searchValue: string;
  setSearchValue: Dispatch<SetStateAction<string>>;
  jobs?: Job[];
  searchParams?: GetJobsInput;
};

export const MobileView = (props: MobileViewProps) => {
  const {
    content,
    tableControls,
    jobs,
    jobActions,
    searchValue,
    setSearchValue,
    searchParams,
  } = props;
  const theme = useTheme();
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);

  const getJobAddress = (jobLocation: JobLocation | undefined) => {
    let fullAddress: string = '-';
    if (jobLocation?.addressLine1) {
      fullAddress = jobLocation.addressLine1;
      if (jobLocation.city) {
        fullAddress += ' ' + jobLocation.city;
      }
      if (jobLocation.postalCode) {
        fullAddress += ' ' + jobLocation.postalCode;
      }
    }
    return fullAddress;
  };

  return (
    <Fragment>
      <Box
        css={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
        onClick={() => setOpenDrawer(true)}
      >
        <SearchIcon />
        <TuneIcon css={{ marginLeft: theme.spacing(1) }} />
      </Box>
      <FullDivider />
      <Fragment>
        <Box p={1}>
          {jobs?.map(job => {
            return (
              <Fragment key={job.applicationNo}>
                <Grid
                  css={{
                    padding: '32px 24px',
                    backgroundColor: job.isOpened
                      ? 'transparent'
                      : colors.riverBlue,
                    borderLeft: job.isOpened
                      ? 'none'
                      : `2px solid ${colors.noirBlur}`,
                  }}
                >
                  <Typography>
                    <Link
                      to={`${routes.PGE_PLUS_INSTALLER_PORTAL_JOB_DETAILS.replace(
                        ':jobId',
                        job.id,
                      )}`}
                      css={{
                        fontWeight: 'bold',
                        textDecoration: 'none',
                        color: colors.noirBlur,
                      }}
                    >
                      {`${content.get('REFERENCE_NO')}: `}
                      <span
                        css={{
                          fontWeight: 400,
                        }}
                      >
                        {job.applicationNo}
                      </span>
                    </Link>
                  </Typography>
                  <Typography>
                    <span
                      css={{
                        fontWeight: 'bold',
                      }}
                    >
                      {`${content.get('REUSABLE_DATE')}: `}
                    </span>
                    <span>{toDateString(job.createdAt, DATE_FORMAT)}</span>
                  </Typography>
                  <Typography>
                    <span
                      css={{
                        fontWeight: 'bold',
                      }}
                    >
                      {`${content.get('CUSTOMER_NAME')}: `}
                    </span>
                    <span>{`${job.jobLocation.firstName} ${job.jobLocation.lastName}`}</span>
                  </Typography>
                  <Typography>
                    <span
                      css={{
                        fontWeight: 'bold',
                      }}
                    >
                      {`${content.get('ADDRESS')}: `}
                    </span>
                    <span>{getJobAddress(job?.jobLocation)}</span>
                  </Typography>
                  <Typography>
                    <span
                      css={{
                        fontWeight: 'bold',
                      }}
                    >
                      {`${content.get('REUSABLE_STATUS')}: `}
                    </span>
                    {jobActions(job)}
                  </Typography>
                </Grid>
                {!job.isOpened && <FullDivider />}
              </Fragment>
            );
          })}
        </Box>
      </Fragment>
      <Drawer
        anchor="bottom"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
      >
        <Box
          css={{
            display: 'flex',
            justifyContent: 'flex-end',
            margin: theme.spacing(1),
          }}
          onClick={() => setOpenDrawer(false)}
        >
          <CloseIcon />
        </Box>
        <Box>
          <Box
            css={{
              marginBottom: theme.spacing(2),
              paddingLeft: theme.spacing(2),
              paddingRight: theme.spacing(2),
            }}
          >
            <Search
              content={content}
              search={val => tableControls(TableControlsActions.SEARCH, val)}
              setOpenDrawer={setOpenDrawer}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
            />
          </Box>
          <FullDivider />
          <Box
            css={{
              marginBottom: theme.spacing(2),
              paddingLeft: theme.spacing(2),
              paddingRight: theme.spacing(2),
            }}
          >
            <Typography
              css={{
                fontSize: theme.spacing(2),
                fontWeight: 'bold',
              }}
            >
              {content.get('FILTER_BY_STATUS')}
            </Typography>
            <Filter
              content={content}
              filter={val => tableControls(TableControlsActions.FILTER, val)}
              setOpenDrawer={setOpenDrawer}
              value={searchParams?.status || undefined}
            />
          </Box>
          <FullDivider />
          <Box
            css={{
              marginBottom: theme.spacing(2),
              paddingLeft: theme.spacing(2),
              paddingRight: theme.spacing(2),
            }}
          >
            <Typography
              css={{
                fontSize: theme.spacing(2),
                fontWeight: 'bold',
              }}
            >
              {content.get('SORT_BY')}
            </Typography>
            <Sort
              content={content}
              sort={val => tableControls(TableControlsActions.SORT, val)}
              value={searchParams?.sort || undefined}
            />
          </Box>
          <FullDivider />
        </Box>
      </Drawer>
    </Fragment>
  );
};
